"use client";

import { useEffect, useState } from "react";

export type PaginatedResult<T> = {
  items: T[];
  total: number;
};

type PaginatedQueryState<T> = PaginatedResult<T> & {
  /** True only before the first successful response (full-page spinner). */
  loading: boolean;
  /** True whenever a request is in flight (including page changes). */
  isFetching: boolean;
  error: string | null;
};

/**
 * Loads a paginated `{ items, total }` list, discarding results from requests
 * that a newer one has already superseded.
 *
 * Fetcher shape matches hey-api generated SDK calls (`{ data?: ... }`).
 * Keeps the previous page of rows visible while a new page loads so the UI
 * does not flash a full-page spinner on every Next/Previous click.
 */
export function usePaginatedQuery<T>(
  fetcher: () => Promise<{ data?: PaginatedResult<T> }>,
  errorMessage: string,
  deps: unknown[] = [],
): PaginatedQueryState<T> {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setIsFetching(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!active) return;
        setItems(result.data?.items ?? []);
        setTotal(result.data?.total ?? 0);
        setHasLoaded(true);
      })
      .catch(() => {
        if (active) setError(errorMessage);
      })
      .finally(() => {
        if (active) setIsFetching(false);
      });

    return () => {
      active = false;
    };
    // The fetcher closes over `deps`; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    items,
    total,
    loading: !hasLoaded && isFetching,
    isFetching,
    error,
  };
}
